/**
 * Settle-after-reply rules for a Merlin edit proposal (kanban 006f1c9e).
 *
 * When a CSR answers a customer from the portal on a thread whose email
 * carries an OPEN Merlin change card, the card usually stays open: measured
 * on production 2026-10-16, 11 of the 13 order_edit_proposal_aging red flags
 * ever raised had a desk reply on the thread BEFORE the flag (AU02393: "I
 * will keep the order as is", 21 minutes after Merlin proposed the add). The
 * pages prompt right after the send; these are the pure rules they share.
 */
import type { ComposeAction } from '../components/portal/EmailComposeModal';
import type { PortalEmailEditProposal, PortalEmailItem } from '../types/portal';

/** Longest dismiss reason the backend stores (OrderEditProposalDismissBody). */
export const DISMISS_REASON_MAX = 500;

/** What the prompt says the reason came from — the learning loop reads it. */
const REASON_PREFIX = 'Answered the customer by email: ';

/**
 * The open proposal a just-sent email may have settled, or null when there is
 * nothing to ask about. A reply / reply-all answers the customer; a forward
 * answers them only when it went back to the email's sender (AU02469: the
 * desk FORWARDED the VP's "they pay the freight" email to him and the rep —
 * that was the answer, no prompt fired, and the card aged into a HIGH flag);
 * a new email never does. Only a `proposed` card is still open.
 */
export function proposalToSettleAfterReply(
  email: Pick<PortalEmailItem, 'edit_proposal' | 'email'> | null | undefined,
  action: ComposeAction,
  toAddresses: readonly string[] = [],
): PortalEmailEditProposal | null {
  if (!answersTheSender(email?.email, action, toAddresses)) return null;
  const proposal = email?.edit_proposal ?? null;
  if (!proposal || proposal.status !== 'proposed') return null;
  return proposal;
}

/** Whether a send of `action` reached the person who wrote the email. */
export function answersTheSender(
  senderAddress: string | null | undefined,
  action: ComposeAction,
  toAddresses: readonly string[],
): boolean {
  if (action === 'reply' || action === 'reply_all') return true;
  if (action !== 'forward') return false;
  const sender = normaliseAddress(senderAddress);
  if (!sender) return false;
  return toAddresses.some((addr) => normaliseAddress(addr) === sender);
}

/** Bare, lower-cased address from "Name <a@x.com>" or "a@x.com". */
function normaliseAddress(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  const angle = raw.match(/<([^>]+)>/);
  return (angle ? angle[1] : raw).trim().toLowerCase();
}

/**
 * Prefill for the dismiss reason: the CSR's own reply, whitespace-collapsed,
 * prefixed so a later reader knows it was an email answer, cut to the
 * backend's limit. Empty reply text → just the prefix-less empty string, so
 * the textarea starts blank rather than with a dangling label.
 */
export function dismissReasonFromReply(replyText: string | null | undefined): string {
  const text = (replyText ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const room = DISMISS_REASON_MAX - REASON_PREFIX.length;
  const body = text.length > room ? `${text.slice(0, room - 1).trimEnd()}…` : text;
  return `${REASON_PREFIX}${body}`;
}
